/**
 * AdminProductDetailPage Component
 *
 * Product detail page for admin panel.
 * Displays comprehensive product information including analytics, sales data, and business insights.
 * Uses React Query for efficient data fetching and caching.
 *
 * Features:
 * - Full product details view
 * - Product analytics (purchase count, revenue, sales trends)
 * - QR code display
 * - Product image and information
 * - Stock and inventory management info
 * - Edit/Update button
 * - Real-time updates with cache invalidation
 */

import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTitle } from "../../hooks/useTitle";
import { useProduct } from "../../hooks/useProducts";
import { useAllOrders } from "../../hooks/useAdmin";
import { useReviewsByProduct } from "../../hooks/useReviews";
import { AdminLayout, useAdminLayout } from "../../components/Layouts/Admin";
import { ProductQRCode } from "../../components/ProductQRCode";
import {
  PageHeader,
  StatusBadge,
  LoadingState,
  ErrorState,
  Card,
} from "../../components/ui";
import { formatDateLong } from "../../utils/formatDate";
import { formatPrice } from "../../utils/formatPrice";
import { getProductImageUrl, getProductImageKey } from "../../utils/productImage";
import { calculateSingleProductAnalytics } from "../../services/analyticsService";
import { Rating } from "../../components";

// Inner component that uses the AdminLayout context
const AdminProductDetailContent = () => {
  const { toggleSidebar } = useAdminLayout();
  const { id: productId } = useParams();
  const navigate = useNavigate();
  
  // Fetch product data
  const { data: product, isLoading: productLoading, error: productError } = useProduct(productId, !!productId);
  
  // Fetch all orders for analytics
  const { data: orders = [], isLoading: ordersLoading } = useAllOrders();
  
  // Fetch reviews for rating calculation
  const {
    data: reviewsData = { reviews: [], ratingStats: { averageRating: 0, reviewCount: 0 } },
    isLoading: reviewsLoading,
  } = useReviewsByProduct(productId, !!productId);

  // Calculate product analytics
  const productAnalytics = useMemo(() => {
    if (!product || !orders || orders.length === 0) {
      return {
        productId: productId || null,
        productName: product?.name || "Unknown",
        purchaseCount: 0,
        totalQuantity: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        orders: [],
      };
    }
    return calculateSingleProductAnalytics(productId, orders, product);
  }, [productId, product, orders]);

  // Generate product URL for QR code
  const productUrl = useMemo(() => {
    const baseUrl = process.env.REACT_APP_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "");
    return `${baseUrl}/products/${productId}`;
  }, [productId]);

  // Show error toast if API call fails
  useEffect(() => {
    if (productError) {
      toast.error(productError.message || "Failed to load product details", {
        closeButton: true,
        position: "bottom-right",
      });
    }
  }, [productError]);

  const isLoading = productLoading || ordersLoading || reviewsLoading;
  const error = productError;

  // Use review stats if available, otherwise fall back to product rating
  const displayRating = reviewsData.ratingStats?.averageRating || product?.rating || 0;
  const reviewCount = reviewsData.ratingStats?.reviewCount || 0;

  return (
    <div className="space-y-6 w-full max-w-full">
      {/* Page Header */}
      <PageHeader
        title="Product Details"
        description="View comprehensive product information and analytics"
        onToggleSidebar={toggleSidebar}
        showBackButton={true}
        onBack={() => navigate("/admin/products")}
      />

      {/* Loading State */}
      {isLoading && <LoadingState message="Loading product details..." />}

      {/* Error State */}
      {error && !isLoading && (
        <ErrorState message={error.message || "Failed to load product details"} />
      )}

      {/* Product Details */}
      {!isLoading && !error && product && (
        <div className="space-y-6">
          {/* Product Overview Card */}
          <Card className="p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Product Image */}
              <div className="flex-shrink-0">
                <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <img
                    className="w-full h-auto rounded-lg object-cover"
                    src={getProductImageUrl(product) || product.poster}
                    key={getProductImageKey(product)}
                    alt={product.name}
                  />
                </div>
              </div>

              {/* Right: Product Information */}
              <div className="space-y-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {product.name}
                  </h1>
                  {product.overview && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {product.overview}
                    </p>
                  )}
                </div>

                {/* Price */}
                <div>
                  <span className="inline-flex items-center px-4 py-2 rounded-lg text-2xl font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800">
                    <i className="bi bi-currency-dollar mr-1.5"></i>
                    {formatPrice(product.price)}
                  </span>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2">
                  <Rating rating={displayRating} />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    ({displayRating.toFixed(1)} out of 5)
                  </span>
                  {reviewCount > 0 && (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      · {reviewCount} {reviewCount === 1 ? "review" : "reviews"}
                    </span>
                  )}
                </div>

                {/* Product Badges */}
                <div className="flex flex-wrap gap-2">
                  {product.best_seller && (
                    <StatusBadge
                      status="best_seller"
                      customLabels={{ best_seller: "Best Seller" }}
                    />
                  )}
                  {product.stock !== undefined ? (
                    <StatusBadge
                      status={product.stock === 0 ? "out_of_stock" : product.stock <= (product.lowStockThreshold || 10) ? "low_stock" : "in_stock"}
                      customLabels={{
                        in_stock: `${product.stock} in stock`,
                        low_stock: `Low Stock (${product.stock})`,
                        out_of_stock: "Out of Stock",
                      }}
                    />
                  ) : (
                    <StatusBadge
                      status={product.in_stock ? "in_stock" : "out_of_stock"}
                    />
                  )}
                  {(product.featured_product === 1 || product.featured_product === true) && (
                    <StatusBadge
                      status="featured"
                      customLabels={{ featured: "Featured" }}
                    />
                  )}
                  {product.size && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                      <i className="bi bi-file-earmark mr-1.5 text-xs"></i>
                      {product.size} MB
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Analytics Card */}
          <Card className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Sales Analytics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  Times Purchased
                </div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">
                  {productAnalytics.purchaseCount}
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <div className="text-sm font-medium text-green-600 dark:text-green-400">
                  Total Quantity Sold
                </div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">
                  {productAnalytics.totalQuantity}
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="text-sm font-medium text-amber-600 dark:text-amber-400">
                  Total Revenue
                </div>
                <div className="text-2xl font-bold text-amber-900 dark:text-amber-100 mt-1">
                  ${productAnalytics.totalRevenue.toFixed(2)}
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="text-sm font-medium text-purple-600 dark:text-purple-400">
                  Avg Order Value
                </div>
                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100 mt-1">
                  ${productAnalytics.averageOrderValue.toFixed(2)}
                </div>
              </div>
            </div>
          </Card>

          {/* Product Information Card */}
          <Card className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Product Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Product ID
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-white font-mono mt-1">
                      {product.id}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Price
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-white mt-1">
                      ${formatPrice(product.price)}
                    </dd>
                  </div>
                  {product.stock !== undefined && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Stock Quantity
                      </dt>
                      <dd className="text-sm text-gray-900 dark:text-white mt-1">
                        {product.stock}
                      </dd>
                    </div>
                  )}
                  {product.lowStockThreshold !== undefined && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Low Stock Threshold
                      </dt>
                      <dd className="text-sm text-gray-900 dark:text-white mt-1">
                        {product.lowStockThreshold}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
              <div>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Status
                    </dt>
                    <dd className="mt-1">
                      {product.stock !== undefined ? (
                        <StatusBadge
                          status={product.stock === 0 ? "out_of_stock" : product.stock <= (product.lowStockThreshold || 10) ? "low_stock" : "in_stock"}
                          customLabels={{
                            in_stock: "In Stock",
                            low_stock: "Low Stock",
                            out_of_stock: "Out of Stock",
                          }}
                        />
                      ) : (
                        <StatusBadge
                          status={product.in_stock ? "in_stock" : "out_of_stock"}
                        />
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Best Seller
                    </dt>
                    <dd className="mt-1">
                      {product.best_seller ? (
                        <StatusBadge
                          status="best_seller"
                          customLabels={{ best_seller: "Yes" }}
                        />
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">No</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Featured
                    </dt>
                    <dd className="mt-1">
                      {(product.featured_product === 1 || product.featured_product === true) ? (
                        <StatusBadge
                          status="featured"
                          customLabels={{ featured: "Yes" }}
                        />
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">No</span>
                      )}
                    </dd>
                  </div>
                  {product.size && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        File Size
                      </dt>
                      <dd className="text-sm text-gray-900 dark:text-white mt-1">
                        {product.size} MB
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
              {/* QR Code Column */}
              {(product.qrCode || productUrl) && (
                <div>
                  <ProductQRCode
                    qrCode={product.qrCode}
                    productUrl={productUrl}
                    productName={product.name}
                    productId={product.id}
                  />
                </div>
              )}
            </div>
          </Card>

          {/* Description Card */}
          {product.long_description && (
            <Card className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Description
              </h2>
              <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                {product.long_description}
              </p>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/admin/products/${product.id}/edit`)}
              className="px-4 py-2 rounded-lg font-medium bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
            >
              <i className="bi bi-pencil mr-2"></i>
              Edit Product
            </button>
            <button
              onClick={() => navigate("/admin/products")}
              className="px-4 py-2 rounded-lg font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Back to Products
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminProductDetailPage = () => {
  useTitle("Admin Product Details");
  const navigate = useNavigate();

  // Check if user is admin before rendering
  useEffect(() => {
    const userRole = sessionStorage.getItem("userRole");
    if (userRole !== "admin") {
      toast.error("Admin access required", {
        closeButton: true,
        position: "bottom-right",
      });
      navigate("/products");
    }
  }, [navigate]);

  return (
    <AdminLayout>
      <AdminProductDetailContent />
    </AdminLayout>
  );
};

